import React, { useCallback, useEffect, useState } from 'react';
import { Rocket, LogOut, RefreshCw, Trash2, Mail, Phone, Globe, Calendar, Lock, FileText, Download, Plus, X, ClipboardList, Newspaper } from 'lucide-react';
import '../styles/pricing.css';
import { QuoteForm } from './QuoteBuilder';
import JournalAdmin from './JournalAdmin';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'boost_admin_token';
const gbpq = (n) => `£${Math.round(n || 0).toLocaleString('en-GB')}`;

const STATUS_META = {
  new: { label: 'New', bg: '#FCE3EE', color: '#E60065' },
  contacted: { label: 'Contacted', bg: '#FFF3D6', color: '#9A6B00' },
  closed: { label: 'Closed', bg: '#DFF5E9', color: '#17A566' },
};

const QUOTE_STATUS_META = {
  draft: { label: 'Draft', bg: '#EDEDF4', color: '#55557A' },
  sent: { label: 'Sent', bg: '#FCE3EE', color: '#E60065' },
  accepted: { label: 'Accepted', bg: '#DFF5E9', color: '#17A566' },
  declined: { label: 'Declined', bg: '#FBE0E0', color: '#C0392B' },
};

function LoginGate({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) throw new Error(res.status === 401 ? 'Incorrect password' : 'Something went wrong');
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ed-login" data-testid="dashboard-login">
      <form className="ed-login-card" onSubmit={submit}>
        <div className="ed-login-icon"><Lock size={22} /></div>
        <h1>Enquiry Dashboard</h1>
        <p>Enter the team password to view website enquiries.</p>
        <input
          type="password"
          placeholder="Team password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          data-testid="dashboard-password-input"
        />
        {error && <p className="ed-login-error" role="alert" data-testid="dashboard-login-error">{error}</p>}
        <button type="submit" className="pp-btn pp-btn-primary" disabled={busy || !password} data-testid="dashboard-login-submit">
          {busy ? 'Checking…' : 'Unlock dashboard'}
        </button>
      </form>
    </div>
  );
}

const parseUtc = (s) => new Date(/Z$|[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}Z`);

function QuoteCard({ quote, onStatus, onDelete }) {
  const meta = QUOTE_STATUS_META[quote.status] || QUOTE_STATUS_META.sent;
  const date = parseUtc(quote.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <article className="ed-card" data-testid={`quote-card-${quote.id}`}>
      <header className="ed-card-head">
        <div>
          <h3>{quote.reference}<span className="ed-biz"> · {quote.name}{quote.business ? ` · ${quote.business}` : ''}</span></h3>
          <span className="ed-date">{date} · {quote.source === 'admin' ? 'Admin-created' : 'Website'}</span>
        </div>
        <span className="ed-badge" style={{ background: meta.bg, color: meta.color }} data-testid={`quote-status-badge-${quote.id}`}>{meta.label}</span>
      </header>
      <div className="ed-meta">
        <a href={`mailto:${quote.email}`}><Mail size={14} /> {quote.email}</a>
        {quote.phone && <span><Phone size={14} /> {quote.phone}</span>}
      </div>
      <div className="ed-tags">
        {quote.subtotal_oneoff_ex > 0 && <span className="ed-tag">One-off: {gbpq(quote.total_oneoff_inc)} inc VAT</span>}
        {quote.subtotal_monthly_ex > 0 && <span className="ed-tag">Monthly: {gbpq(quote.total_monthly_inc)} inc VAT/mo</span>}
      </div>
      <ul className="ed-quote-lines">
        {(quote.lines || []).map((l, i) => (
          <li key={i}>{l.label} — {gbpq(l.line_total_ex)}{l.unit === 'monthly' ? '/mo' : ''} + VAT</li>
        ))}
      </ul>
      {quote.notes && <p className="ed-details">{quote.notes}</p>}
      <footer className="ed-card-actions">
        <select
          value={quote.status || 'sent'}
          onChange={(e) => onStatus(quote.id, e.target.value)}
          data-testid={`quote-status-select-${quote.id}`}
        >
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="accepted">Accepted</option>
          <option value="declined">Declined</option>
        </select>
        <a className="ed-quote-pdf" href={`${API}/api/quote/${quote.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`quote-download-${quote.id}`}>
          <Download size={14} /> PDF
        </a>
        <button className="ed-delete" onClick={() => onDelete(quote.id)} data-testid={`quote-delete-${quote.id}`}>
          <Trash2 size={14} /> Delete
        </button>
      </footer>
    </article>
  );
}

function EnquiryCard({ enquiry, onStatus, onDelete }) {
  const meta = STATUS_META[enquiry.status] || STATUS_META.new;
  const date = parseUtc(enquiry.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <article className="ed-card" data-testid={`enquiry-card-${enquiry.id}`}>
      <header className="ed-card-head">
        <div>
          <h3>{enquiry.name}{enquiry.business ? <span className="ed-biz"> · {enquiry.business}</span> : null}</h3>
          <span className="ed-date">{date}</span>
        </div>
        <span className="ed-badge" style={{ background: meta.bg, color: meta.color }} data-testid={`enquiry-status-badge-${enquiry.id}`}>{meta.label}</span>
      </header>
      <div className="ed-meta">
        <a href={`mailto:${enquiry.email}`}><Mail size={14} /> {enquiry.email}</a>
        {enquiry.phone && <span><Phone size={14} /> {enquiry.phone}</span>}
        {enquiry.website_url && <a href={enquiry.website_url} target="_blank" rel="noreferrer"><Globe size={14} /> {enquiry.website_url}</a>}
        {enquiry.launch_date && <span><Calendar size={14} /> {enquiry.launch_date}</span>}
      </div>
      {(enquiry.project_type || enquiry.budget || enquiry.marketing_investment) && (
        <div className="ed-tags">
          {enquiry.project_type && <span className="ed-tag">{enquiry.project_type}</span>}
          {enquiry.budget && <span className="ed-tag">{enquiry.budget}</span>}
          {enquiry.marketing_investment && <span className="ed-tag">Marketing: {enquiry.marketing_investment}</span>}
        </div>
      )}
      <p className="ed-details">{enquiry.details}</p>
      <footer className="ed-card-actions">
        <select
          value={enquiry.status || 'new'}
          onChange={(e) => onStatus(enquiry.id, e.target.value)}
          data-testid={`enquiry-status-select-${enquiry.id}`}
        >
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="closed">Closed</option>
        </select>
        <button className="ed-delete" onClick={() => onDelete(enquiry.id)} data-testid={`enquiry-delete-${enquiry.id}`}>
          <Trash2 size={14} /> Delete
        </button>
      </footer>
    </article>
  );
}

export default function EnquiryDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [view, setView] = useState('enquiries');
  const [quotes, setQuotes] = useState(null);
  const [showNewQuote, setShowNewQuote] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEnquiries(null);
  }, []);

  const load = useCallback(async (tk) => {
    setLoading(true);
    setActionError('');
    try {
      const res = await fetch(`${API}/api/website-enquiry`, { headers: { Authorization: `Bearer ${tk}` } });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (!Array.isArray(data)) throw new Error();
      setEnquiries(data);
    } catch {
      setActionError('Could not load enquiries. Check your connection and hit Refresh.');
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const setStatus = async (id, status) => {
    const prevStatus = enquiries.find((e) => e.id === id)?.status || 'new';
    setActionError('');
    setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
    try {
      const res = await fetch(`${API}/api/website-enquiry/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) throw new Error();
    } catch {
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status: prevStatus } : e)));
      setActionError('Could not save the status change. Check your connection and try again.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry permanently?')) return;
    setActionError('');
    try {
      const res = await fetch(`${API}/api/website-enquiry/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) throw new Error();
      setEnquiries((list) => list.filter((e) => e.id !== id));
    } catch {
      setActionError('Could not delete the enquiry. Check your connection and try again.');
    }
  };

  const loadQuotes = useCallback(async (tk) => {
    setActionError('');
    try {
      const res = await fetch(`${API}/api/quotes`, { headers: { Authorization: `Bearer ${tk}` } });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setQuotes(Array.isArray(data) ? data : []);
    } catch {
      setActionError('Could not load quotes. Check your connection and hit Refresh.');
    }
  }, [logout]);

  useEffect(() => { if (token && view === 'quotes' && quotes === null) loadQuotes(token); }, [token, view, quotes, loadQuotes]);

  const setQuoteStatus = async (id, status) => {
    const prev = quotes.find((q) => q.id === id)?.status;
    setQuotes((list) => list.map((q) => (q.id === id ? { ...q, status } : q)));
    try {
      const res = await fetch(`${API}/api/quote/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) throw new Error();
    } catch {
      setQuotes((list) => list.map((q) => (q.id === id ? { ...q, status: prev } : q)));
      setActionError('Could not update the quote status.');
    }
  };

  const removeQuote = async (id) => {
    if (!window.confirm('Delete this quote permanently?')) return;
    try {
      const res = await fetch(`${API}/api/quote/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) throw new Error();
      setQuotes((list) => list.filter((q) => q.id !== id));
    } catch {
      setActionError('Could not delete the quote.');
    }
  };

  if (!token) return <LoginGate onLogin={setToken} />;

  const newCount = enquiries ? enquiries.filter((e) => (e.status || 'new') === 'new').length : 0;
  const projectTypes = enquiries
    ? [...new Set(enquiries.map((e) => e.project_type).filter(Boolean))].sort()
    : [];
  const visibleEnquiries = enquiries
    ? (typeFilter === 'all' ? enquiries : enquiries.filter((e) => e.project_type === typeFilter))
    : null;

  return (
    <div className="ed-page" data-testid="enquiry-dashboard">
      <header className="ed-header">
        <div className="ed-header-inner">
          <div className="ed-title">
            <Rocket size={20} />
            <h1>{view === 'quotes' ? 'Quotes' : view === 'journal' ? 'The Journal' : 'Website Enquiries'}</h1>
            {view === 'enquiries' && enquiries && <span className="ed-count" data-testid="dashboard-count">{enquiries.length} total · {newCount} new</span>}
            {view === 'quotes' && quotes && <span className="ed-count" data-testid="dashboard-quote-count">{quotes.length} total</span>}
          </div>
          <div className="ed-header-actions">
            <div className="ed-tabs" data-testid="dashboard-tabs">
              <button className={view === 'enquiries' ? 'ed-tab-on' : ''} onClick={() => setView('enquiries')} data-testid="dashboard-tab-enquiries"><ClipboardList size={15} /> Enquiries</button>
              <button className={view === 'quotes' ? 'ed-tab-on' : ''} onClick={() => setView('quotes')} data-testid="dashboard-tab-quotes"><FileText size={15} /> Quotes</button>
              <button className={view === 'journal' ? 'ed-tab-on' : ''} onClick={() => setView('journal')} data-testid="dashboard-tab-journal"><Newspaper size={15} /> Journal</button>
            </div>
            {view === 'enquiries' && enquiries && enquiries.length > 0 && (
              <select
                className="ed-filter"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                data-testid="dashboard-type-filter"
                aria-label="Filter enquiries by service type"
              >
                <option value="all">All service types ({enquiries.length})</option>
                {projectTypes.map((t) => (
                  <option key={t} value={t}>
                    {t} ({enquiries.filter((e) => e.project_type === t).length})
                  </option>
                ))}
              </select>
            )}
            {view === 'quotes' && (
              <button onClick={() => setShowNewQuote((s) => !s)} data-testid="dashboard-new-quote">
                {showNewQuote ? <><X size={15} /> Close</> : <><Plus size={15} /> New quote</>}
              </button>
            )}
            {view !== 'journal' && (
              <button onClick={() => (view === 'quotes' ? loadQuotes(token) : load(token))} disabled={loading} data-testid="dashboard-refresh"><RefreshCw size={15} /> Refresh</button>
            )}
            <button onClick={logout} data-testid="dashboard-logout"><LogOut size={15} /> Log out</button>
          </div>
        </div>
      </header>
      <main className={`ed-main${view === 'journal' ? ' ed-main-wide' : ''}`}>
        {actionError && <p className="ed-action-error" role="alert" data-testid="dashboard-action-error">{actionError}</p>}

        {view === 'enquiries' && (
          <>
            {loading && !enquiries && <p className="ed-empty">Loading enquiries…</p>}
            {enquiries && enquiries.length === 0 && (
              <p className="ed-empty" data-testid="dashboard-empty">No enquiries yet. They'll appear here as soon as someone submits the pricing page form. 🚀</p>
            )}
            {enquiries && enquiries.length > 0 && visibleEnquiries.length === 0 && (
              <p className="ed-empty" data-testid="dashboard-filter-empty">No enquiries match this service type. Try a different filter.</p>
            )}
            {visibleEnquiries && visibleEnquiries.map((e) => (
              <EnquiryCard key={e.id} enquiry={e} onStatus={setStatus} onDelete={remove} />
            ))}
          </>
        )}

        {view === 'quotes' && (
          <>
            {showNewQuote && (
              <div className="ed-newquote" data-testid="dashboard-newquote-panel">
                <h2 className="pp-display" style={{ fontSize: 22, margin: '0 0 14px' }}>Build a new quote</h2>
                <QuoteForm
                  adminToken={token}
                  onCreated={() => { setShowNewQuote(false); loadQuotes(token); }}
                />
              </div>
            )}
            {quotes === null && <p className="ed-empty">Loading quotes…</p>}
            {quotes && quotes.length === 0 && !showNewQuote && (
              <p className="ed-empty" data-testid="dashboard-quotes-empty">No quotes yet. Website visitors' quotes will appear here — or hit "New quote" to build one.</p>
            )}
            {quotes && quotes.map((q) => (
              <QuoteCard key={q.id} quote={q} onStatus={setQuoteStatus} onDelete={removeQuote} />
            ))}
          </>
        )}
        {view === 'journal' && <JournalAdmin token={token} onUnauthorised={logout} />}
      </main>
    </div>
  );
}
